import { yupResolver } from '@hookform/resolvers/yup';
import { Backdrop, Stack, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { SubmitHandler, useForm, useFormContext } from 'react-hook-form';

import { EmailBasicDto } from '@/api/generated';
import { mapAttachmentItemToNewEmailAttachment } from '@/components/emailWindow/components/attachments/model/NewEmailAttachment.model.ts';
import { NewEmailAutosaveAlert } from '@/components/emailWindow/components/newEmail/components/NewEmailAutosaveAlert.comp.tsx';
import { NewEmailForm } from '@/components/emailWindow/components/newEmail/components/NewEmailForm.comp.tsx';
import { NewEmailHeader } from '@/components/emailWindow/components/newEmail/components/NewEmailHeader.comp.tsx';
import {
  initNewEmailFormModel,
  NewEmailFormModel,
} from '@/components/emailWindow/model/NewEmailForm.model.ts';
import { NewEmailStyles } from '@/components/emailWindow/styles/NewEmail.styles.ts';
import { useCreateEmailDraft } from '@/components/emailWindow/util/useCreateEmailDraft.util.ts';
import {
  AutosaveStopReason,
  useEmailDraftController,
} from '@/components/emailWindow/util/useEmailDraftController.util.ts';
import { useNewEmailValidation } from '@/components/emailWindow/util/useNewEmail.validation.ts';
import { FormProviderRc } from '@/components/form/FormProviderRC.comp.tsx';
import { TOP_BAR_HEIGHT } from '@/components/layout/topBar/TopBar.styles.ts';
import { FullPageSpinner } from '@/components/ui/fullPageSpinner/FullPageSpinner.comp.tsx';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { GlobalEvent, globalEventDispatcher } from '@/utils/globalEventDispatcher.util.ts';
import { useReadEmail } from '@/views/emails/components/threadEmails/util/useReadEmail.util.ts';
import {
  EditedEmailState,
  NEW_EMAIL_REDUCER_EVENT,
  NEW_EMAIL_VIEW_MODE,
  useNewEmailDraftInitializerState,
  useNewEmailReadyViewState,
  useNewEmailUpdateState,
  useNewEmailWindowMode,
} from '@/views/emails/context/NewEmails.ts';

import { NewEmailBeingSent } from './components/NewEmailBeingSent.comp';

const EMAIL_WINDOW_Z_INDEX = (theme: Theme): number => theme.zIndex.drawer + 1;

export const NewEmail: React.FC = () => {
  const draftInitializerState = useNewEmailDraftInitializerState();
  const dispatchNewEmailState = useNewEmailUpdateState();
  const { createEmailDraft } = useCreateEmailDraft();
  const { email, userId } = useUserContext();

  const { email: fetchedEmail, isFetching } = useReadEmail({
    emailId:
      draftInitializerState?.state === EditedEmailState.READY
        ? (draftInitializerState.emailId ?? '')
        : '',
    isEnabled: draftInitializerState?.state === EditedEmailState.READY,
  });

  useEffect(() => {
    if (
      !draftInitializerState ||
      draftInitializerState.sourceEmailIsDraft ||
      draftInitializerState.state === EditedEmailState.READY
    ) {
      return;
    }

    const createDraft = async () => {
      const res = await createEmailDraft({
        data: {
          from: email,
          onEmailId: draftInitializerState.onEmailId,
          sendMode: draftInitializerState.mode,
        },
        resource: draftInitializerState.resource,
        userId: userId,
      });
      dispatchNewEmailState({
        payload: {
          draftRequestId: draftInitializerState.draftRequestId,
          emailId: res.emailId,
          threadId: res.emailThreadId,
        },
        type: NEW_EMAIL_REDUCER_EVENT.DRAFT_READY,
      });
    };

    void createDraft();
  }, [createEmailDraft, dispatchNewEmailState, draftInitializerState, email, userId]);

  useEffect(() => {
    if (fetchedEmail?.attachments && !isFetching) {
      const attachments = fetchedEmail?.attachments.map(it =>
        mapAttachmentItemToNewEmailAttachment(it, true),
      );
      dispatchNewEmailState({
        payload: attachments,
        type: NEW_EMAIL_REDUCER_EVENT.ATTACHMENT_SET,
      });
    }
  }, [dispatchNewEmailState, fetchedEmail?.attachments, isFetching]);

  if (isFetching) {
    return (
      <Backdrop aria-busy="true" aria-label="Loading" open sx={{ zIndex: EMAIL_WINDOW_Z_INDEX }}>
        <FullPageSpinner />
      </Backdrop>
    );
  }

  if (draftInitializerState?.state === EditedEmailState.READY && fetchedEmail) {
    return <NewEmailInner fetchedEmail={fetchedEmail} />;
  }
};

type NewEmailInnerProps = {
  fetchedEmail: EmailBasicDto | undefined;
};

export const NewEmailInner: React.FC<NewEmailInnerProps> = ({ fetchedEmail }) => {
  const readyViewState = useNewEmailReadyViewState();
  const updateNewEmailState = useNewEmailUpdateState();
  const validation = useNewEmailValidation();
  const { email } = useUserContext();
  const [isCollapsed, setIsCollapsed] = useState(readyViewState?.isCollapsed ?? false);
  const formMethods = useForm<NewEmailFormModel>({
    defaultValues: initNewEmailFormModel({
      email: fetchedEmail,
      userEmail: email,
    }),
    resolver: yupResolver(validation),
  });

  const { autosaveStopped, isSendingEmail, saveCurrentDraft, sendDraft } = useEmailDraftController({
    getFormValues: formMethods.getValues,
  });

  const handleClose = async () => {
    await saveCurrentDraft('final');
    globalEventDispatcher(GlobalEvent.EMAIL_DRAFT_UPDATED);
    updateNewEmailState({
      payload: {},
      type: NEW_EMAIL_REDUCER_EVENT.CLEAR_COMPOSER,
    });
  };

  const handleSubmit: SubmitHandler<NewEmailFormModel> = async data => {
    const wasEmailSent = await sendDraft(data);
    if (!wasEmailSent) {
      return;
    }
    globalEventDispatcher(GlobalEvent.EMAIL_SENT);
    globalEventDispatcher(GlobalEvent.EMAIL_DRAFT_UPDATED);
    updateNewEmailState({
      payload: {},
      type: NEW_EMAIL_REDUCER_EVENT.SEND_SUCCESS,
    });
  };

  if (readyViewState?.windowMode === NEW_EMAIL_VIEW_MODE.DOCKED) {
    return (
      <FormProviderRc {...formMethods} validation={validation}>
        <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
          <FormBody
            autosaveStopped={autosaveStopped}
            draftId={readyViewState.emailId}
            isCollapsed={isCollapsed}
            isSendingEmail={isSendingEmail}
            onClose={handleClose}
            setIsCollapsed={setIsCollapsed}
          />
        </form>
      </FormProviderRc>
    );
  }

  if (readyViewState?.windowMode === NEW_EMAIL_VIEW_MODE.FLOATING_WINDOW) {
    return (
      <FloatingWrapper isCollapsed={isCollapsed}>
        <FormProviderRc {...formMethods} validation={validation}>
          <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
            <FormBody
              autosaveStopped={autosaveStopped}
              draftId={readyViewState.emailId}
              isCollapsed={isCollapsed}
              isSendingEmail={isSendingEmail}
              onClose={handleClose}
              setIsCollapsed={setIsCollapsed}
            />
          </form>
        </FormProviderRc>
      </FloatingWrapper>
    );
  }
};

type FormProps = {
  autosaveStopped: AutosaveStopReason | null;
  draftId: string;
  isCollapsed: boolean;
  isSendingEmail: boolean;
  onClose: () => void;
  setIsCollapsed: Dispatch<SetStateAction<boolean>>;
};

const FormBody: React.FC<FormProps> = ({
  autosaveStopped,
  draftId,
  isCollapsed,
  isSendingEmail,
  onClose,
  setIsCollapsed,
}) => {
  const updateNewEmailState = useNewEmailUpdateState();
  const windowMode = useNewEmailWindowMode();
  const { getValues } = useFormContext<NewEmailFormModel>();

  const handleCollapseClick = () => {
    if (windowMode !== NEW_EMAIL_VIEW_MODE.FLOATING_WINDOW) {
      const form = getValues();
      updateNewEmailState({
        payload: { form, isCollapsed: true, mode: NEW_EMAIL_VIEW_MODE.FLOATING_WINDOW },
        type: NEW_EMAIL_REDUCER_EVENT.CHANGE_WINDOW_MODE,
      });
    }
    setIsCollapsed(cur => !cur);
  };

  return (
    <Stack sx={NewEmailStyles}>
      <NewEmailHeader
        isCollapsed={isCollapsed}
        isSendingEmail={isSendingEmail}
        onClose={onClose}
        onCollapseClick={handleCollapseClick}
      />
      {!isCollapsed && (
        <Box sx={{ maxHeight: isCollapsed ? '0px' : '100%', position: 'relative' }}>
          <NewEmailAutosaveAlert reason={autosaveStopped} />
          <NewEmailForm draftId={draftId} isSendingEmail={isSendingEmail} />
          <NewEmailBeingSent isSending={isSendingEmail} />
        </Box>
      )}
    </Stack>
  );
};

type FloatingWrapperProps = {
  children: React.ReactNode;
  isCollapsed: boolean;
};

const FloatingWrapper: React.FC<FloatingWrapperProps> = ({ children, isCollapsed }) => {
  return (
    <>
      <Backdrop invisible={isCollapsed} open sx={{ height: isCollapsed ? 0 : 'inherit' }} />
      <Box
        sx={{
          bottom: 0,
          left: 0,
          position: 'absolute',
          right: 0,
          top: isCollapsed ? undefined : 0 + TOP_BAR_HEIGHT,
          zIndex: EMAIL_WINDOW_Z_INDEX,
        }}
      >
        {children}
      </Box>
    </>
  );
};
